<template>
    <div class="container admin-table-page">
        <!-- Fila del título y flecha hacia atrás -->
        <div class="param-title-wrap plan-cuentas-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- Fila de filtros debajo del título a la izquierda -->
        <div class="param-title-wrap plan-cuentas-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <b-form-select
                        id="report-select"
                        v-model="selReporte"
                        @change="iniciaRedefinicion; fgl_consulta = false"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>
                        <option v-for="reporte in reportes" :key="reporte.codigo" :value="reporte.codigo">
                            {{ reporte?.codigo + " - " + reporte?.nombre }}
                        </option>
                    </b-form-select>
                    
                    <b-form-select
                        id="validity-select"
                        v-model="selVigencia"
                        @change="iniciaRedefinicion; fgl_consulta = false"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option v-for="vigencia in listVigencia" :key="vigencia.vigencia" :value="vigencia.vigencia">
                            {{ vigencia?.vigencia }}
                        </option>
                    </b-form-select>
                    
                    <b-button
                        class="consult-btn ml-2"
                        @click="consultaRedefinicionFilas(); fgl_consulta = true"
                        :disabled="!selVigencia || !selReporte"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>
            </div>
        </div>

        <div class="admin-table-block mt-4">
            <!-- Spinner de carga -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>

            <!-- ESTADO INICIAL - Sin datos -->
            <div v-else-if="!fgl_consulta && !isLoading && listRedefinicionFila.length === 0">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="490"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted">
                                {{ labels.selectFilters }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA CON DATOS -->
            <div v-else-if="fgl_consulta && listRedefinicionFila.length > 0 && !isLoading">
                <CorporateDataTable
                    ref="corporateTable"
                    :items="visibleItems"
                    :fields="allFields"
                    :filter-defs="tableFilterDefs"
                    :labels="tableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="490"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :toolbar-disabled="false"
                >
                    <template #toolbar-actions>
                        <div class="plan-cuentas-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="creaRedFila()"
                                :disabled="isVigenciaBloqueada"
                            >
                                {{ labels.create }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(niif)="data">
                        <div class="text-center"><strong>{{ data.item?.niif }}</strong></div>
                    </template>

                    <template #cell(descripcion)="data">
                        <div class="text-left">{{ data.item?.descripcion }}</div>
                    </template>

                    <template #cell(primer_nivel)="data">
                        <div class="text-left">{{ namePrimerNivel(data.item?.primer_nivel) }}</div>
                    </template>

                    <template #cell(segundo_nivel)="data">
                        <div class="text-left">{{ nameSegundoNivel(data.item?.primer_nivel, data.item?.segundo_nivel) }}</div>
                    </template>

                    <template #cell(formulacion)="data">
                        <div class="text-left" :title="data.item?.formulacion">
                            {{ truncateFormulation(data.item?.formulacion) }}
                        </div>
                    </template>

                    <!-- Icono de Edición -->
                    <template #cell(accionEdit)="data">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px;"
                            class="md-fab md-primary table-action-btn"
                            @click.stop="editRedFila(data.item.id)"
                            :disabled="isVigenciaBloqueada"
                        >
                            <i><font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/></i>
                        </b-button>
                    </template>

                    <!-- Icono de Eliminación -->
                    <template #cell(accionDelete)="data">
                        <b-button
                            class="reset-btn-danger table-action-btn"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center;"
                            @click.stop="deleteRedFila(data.item.id)"
                            :disabled="isVigenciaBloqueada"
                        >
                            <i><font-awesome-icon :icon="['fas', 'trash']" size="xs"/></i>
                        </b-button>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA VACÍA - Consulta realizada sin resultados -->
            <div v-else-if="fgl_consulta && listRedefinicionFila.length === 0 && !isLoading" class="text-center py-5">
                <font-awesome-icon
                    :icon="['fas', 'table']"
                    size="4x"
                    class="text-muted mb-3"
                    style="opacity: 0.3;"
                />
                <p class="h4 text-muted">{{ labels.noData }}</p>
            </div>
        </div>

        <!-- Modales -->
        <CreaRedefinicionFila
            :modCreaRedFila="modCreaRedFila"
            :vigenciaSelected="selVigencia"
            :codReporte="selReporte"
            @cierraModal="modCreaRedFila = false"
            @reload="consultaRedefinicionFilas"
        />
        <EditaRedefinicionFila
            :modEditaRedFila="modEditaRedFila"
            :dataRedFila="itemRedFila"
            :vigenciaSelected="selVigencia"
            :codReporte="selReporte"
            @cierraModal="modEditaRedFila = false"
            @reload="consultaRedefinicionFilas"
        />
        <InacticaItem :InactivaItem="deletFila" @confirmDelete="confirmDel" @cierraModal="closeModal" />
    </div>
</template>

<script>
import RedefinicionFila from '../../../services/RedefinicionFila'; 
import PrimerNivel from '../../../services/PrimerNivel'; 
import SegundoNivel from '../../../services/SegundoNivel'; 
import CreaRedefinicionFila from './CreaRedefinicionFila.vue'; 
import EditaRedefinicionFila from './EditaRedefinicionFila.vue'; 
import PlanConsolidacion from '../../../services/PlanConsolidacion'; 
import TipoReporte from '../../../services/TipoReporte'; 
import InacticaItem from '../../utils/InacticaItem.vue' 
import CorporateDataTable from "../../utils/CorporateDataTable.vue"

export default {
    name: "RedefinicionFilas",
    components: {
        CreaRedefinicionFila,
        EditaRedefinicionFila,
        InacticaItem,
        CorporateDataTable
    },
    data: () => ({
        listVigencia: [],
        selVigencia: "",
        selReporte: "",
        listRedefinicionFila: [],
        listRedefinicionFilaOrigen: [],
        firstLevSel: "",
        listFirstLev: [],
        listSecondLev: [],
        modCreaRedFila: false,
        modEditaRedFila: false,
        itemRedFila: [],
        reportes: [],
        showTblRedFilas: false,
        deletFila: false,
        rowId: '',
        fgl_consulta: false,
        isLoading: false,
        visibleItems: [],
    }),
    computed: {
        isVigenciaBloqueada() {
            return this.selVigencia ? !this.$store.getters.isPeriodoEnabled(this.selVigencia) : false
        },
        labels() {
            if (this.$store.state._language == "es-ES") {
                return {
                    title: "Redefinición Filas",
                    save: "Guardar",
                    firstLevel: "Primer Nivel",
                    secondLevel: "Segundo Nivel",
                    formulacion: "Formulación",
                    niif: "NIIF",
                    description: "Descripción",
                    editar: "Editar",
                    eliminar: "Eliminar",
                    validity: "Vigencia:",
                    reporte: "Reporte:",
                    select: "Seleccione...",
                    selectReport: "Seleccione Reporte",
                    selectValidity: "Seleccione Vigencia",
                    consultar: "Consultar",
                    search: "Buscar",
                    create: "Crear",
                    clear: "Limpiar",
                    showing: "Mostrando",
                    of: "de",
                    records: "registros",
                    noData: "No hay datos disponibles",
                    noConsult: "Realice una consulta para ver los datos",
                    loading: "Cargando...",
                    selectFilters: "Seleccione los filtros y consulte",
                    filterNiif: "Filtrar NIIF...",
                    filterDescription: "Filtrar Descripción...",
                    searchPlaceholder: "Buscar en la tabla...",
                    rowsPerPage: "Filas por página:",
                    noSort: "Sin orden",
                    sortAsc: "A-Z / Menor a mayor",
                    sortDesc: "Z-A / Mayor a menor",
                    columns: "Columnas",
                    showAllColumns: "Mostrar todas",
                    filters: "Filtros",
                    clearFilters: "Limpiar filtros",
                    noMatches: "No se encontraron resultados",
                }
            } else {
                return {
                    title: "Row redefinition",
                    save: "Save",
                    firstLevel: "First Level",
                    secondLevel: "Second Level",
                    formulacion: "Formulation",
                    niif: "NIIF",
                    description: "Description",
                    editar: "Edit",
                    eliminar: "Delete",
                    validity: "Validity",
                    reporte: "Report",
                    select: "Select...",
                    selectReport: "Select Report",
                    selectValidity: "Select Validity",
                    consultar: "Consult",
                    search: "Search",
                    create: "Create",
                    clear: "Clear",
                    showing: "Showing",
                    of: "of",
                    records: "records",
                    noData: "No data available",
                    noConsult: "Perform a query to see the data",
                    loading: "Loading...",
                    selectFilters: "Select filters and consult",
                    filterNiif: "Filter NIIF...",
                    filterDescription: "Filter Description...",
                    searchPlaceholder: "Search in table...",
                    rowsPerPage: "Rows per page:",
                    noSort: "No sorting",
                    sortAsc: "A-Z / Low to high",
                    sortDesc: "Z-A / High to low",
                    columns: "Columns",
                    showAllColumns: "Show all",
                    filters: "Filters",
                    clearFilters: "Clear filters",
                    noMatches: "No results found",
                }
            }
        },
        allFields() {
            return [
                { key: 'niif', label: this.labels.niif, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'descripcion', label: this.labels.description, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'primer_nivel', label: this.labels.firstLevel, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'segundo_nivel', label: this.labels.secondLevel, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'formulacion', label: this.labels.formulacion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionEdit', label: this.labels.editar, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionDelete', label: this.labels.eliminar, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },
        tableFilterDefs() {
            return [
                { key: 'niif', type: 'text', placeholder: this.labels.filterNiif },
                { key: 'descripcion', type: 'text', placeholder: this.labels.filterDescription }
            ]
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        }
    },
    methods: {
        goBack() {
            this.$router.back()
        },
        
        iniciaRedefinicion() {
            this.listRedefinicionFila = []
            this.visibleItems = []
            this.showTblRedFilas = false
        },
        
        consultaRedefinicionFilas() {
            if (this.selVigencia && this.selReporte) {
                this.isLoading = true;
                this.fgl_consulta = true;
                
                Promise.all([ 
                    PrimerNivel.getFirstLevelByReporte(this.selReporte),
                    SegundoNivel.getAllLevelSecond(this.selReporte),
                    RedefinicionFila.getAllRedefinicionFilas(this.selReporte)
                ])
                .then(([firstLevelRes, secondLevelRes, redefinicionRes]) => { 
                    this.listFirstLev = firstLevelRes.data.firstLevByReport || [];
                    this.listSecondLev = secondLevelRes.data.segundoNivel || [];
                    this.listRedefinicionFila = redefinicionRes.data.redefinicionFila || [];
                    this.listRedefinicionFilaOrigen = [...this.listRedefinicionFila];
                    this.showTblRedFilas = true;
                    this.visibleItems = [...this.listRedefinicionFila];
                })
                .catch((err) => { 
                    console.log(err); 
                    this.$toast.error('Error al consultar las redefiniciones de filas');
                    this.fgl_consulta = true;
                    this.listRedefinicionFila = [];
                    this.visibleItems = [];
                })
                .finally(() => { 
                    this.isLoading = false;
                });
            } else {
                this.$toast.error('Debe seleccionar reporte y vigencia');
            }
        },
        
        truncateFormulation(formulation) {
            if (!formulation) return '';
            if (formulation.length > 50) { 
                const niifMatch = formulation.match(/^(Niif\([^)]+\))/i); 
                if (niifMatch) {
                    const niifPart = niifMatch[1];
                    if (niifPart.length > 50) { 
                        const numbersMatch = niifPart.match(/Niif\(([^)]+)\)/i); 
                        if (numbersMatch) {
                            const numbers = numbersMatch[1].split(','); 
                            if (numbers.length > 8) { 
                                const truncatedNumbers = [
                                    ...numbers.slice(0, 5), 
                                    '...', 
                                    ...numbers.slice(-3) 
                                ].join(','); 
                                return `Niif(${truncatedNumbers})`;
                            }
                        }
                    }
                    return niifPart;
                }
                // Intentar extraer Cuenta()
                const cuentaMatch = formulation.match(/^(Cuenta\([^)]+\))/i);
                if (cuentaMatch) {
                    const cuentaPart = cuentaMatch[1];
                    if (cuentaPart.length > 50) {
                        const numbersMatch = cuentaPart.match(/Cuenta\(([^)]+)\)/i);
                        if (numbersMatch) {
                            const numbers = numbersMatch[1].split(',');
                            if (numbers.length > 8) {
                                const truncatedNumbers = [
                                    ...numbers.slice(0, 5),
                                    '...',
                                    ...numbers.slice(-3)
                                ].join(',');
                                return `Cuenta(${truncatedNumbers})`;
                            }
                        }
                    }
                    return cuentaPart;
                }
                return formulation?.substring(0, 50) + '..? ';             
            }
            return formulation;
        },
        
        closeModal() {
            this.modCreaRedFila = false;
            this.modEditaRedFila = false;
            this.deletFila = false;
        },
        
        namePrimerNivel(idFirstLev) {
            let name = this.listFirstLev.find((firstLev) => firstLev.primer_nivel == idFirstLev)
            return name == undefined ? idFirstLev : name?.descripcion         
        },
        
        nameSegundoNivel(idFirstLev, idSecondLev) {
            let name = this.listSecondLev.find((secondLev) => secondLev.primer_nivel == idFirstLev && secondLev.segundo_nivel == idSecondLev)
            return name != undefined ? name?.descripcion : idSecondLev
        },
        
        creaRedFila() {
            if (!this.fgl_consulta) {
                this.$toast.error('Primero debe realizar una consulta.');
                return;
            }
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.');
                return;
            }
            if (this.selVigencia && this.selReporte) {
                this.modCreaRedFila = true;
            } else {
                this.$toast.error('Debe seleccionar reporte y vigencia primero');
            }
        },
        
        editRedFila(id) {
            if (!this.fgl_consulta) {
                this.$toast.error('Primero debe realizar una consulta.');
                return;
            }
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.');
                return;
            }
            RedefinicionFila.getAllFilaById(id) 
                .then((res) => { 
                    this.itemRedFila = res.data.redefinicionFilaById || [];
                    if (this.itemRedFila[0] && this.itemRedFila[0].listaNiif && this.itemRedFila[0].listaNiif.length > 0) {
                        this.itemRedFila[0].listaNiif = this.itemRedFila[0].listaNiif.map(n => parseInt(n));
                    }
                    if (this.itemRedFila[0] && (!this.itemRedFila[0].listaNiif || this.itemRedFila[0].listaNiif.length === 0) && this.itemRedFila[0].formulacion) {
                        this.itemRedFila[0].listaNiif = this.extractNiifCodesFromFormulation(this.itemRedFila[0].formulacion);
                    }
                    // Extraer cuentas de la formulación
                    if (this.itemRedFila[0] && this.itemRedFila[0].formulacion) {
                        this.itemRedFila[0].listaCuentas = this.extractCuentaCodesFromFormulation(this.itemRedFila[0].formulacion);
                    }
                    this.modEditaRedFila = true;
                })
                .catch((err) => { 
                    console.log(err); 
                    this.$toast.error('Error al cargar los datos de la fila');
                })
        },
        
        extractNiifCodesFromFormulation(formulation) {
            if (!formulation) return [];
            const niifMatch = formulation.match(/Niif\(([^)]+)\)/i); 
            if (niifMatch) {
                return niifMatch[1].split(',')?.map(num => parseInt(num.trim()))?.filter(num => !isNaN(num));             
            }
            return formulation?.replace(/[+-]/g, ',').split(',')?.map(n => parseInt(n.trim()))?.filter(n => !isNaN(n));         
        },

        extractCuentaCodesFromFormulation(formulation) {
            if (!formulation) return [];
            const cuentaMatch = formulation.match(/Cuenta\(([^)]+)\)/i);
            if (cuentaMatch) {
                return cuentaMatch[1].split(',').map(c => c.trim()).filter(c => c);
            }
            return [];
        },
        
        deleteRedFila(id) {
            if (!this.fgl_consulta) {
                this.$toast.error('Primero debe realizar una consulta.');
                return;
            }
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.');
                return;
            }
            this.rowId = id
            this.deletFila = true
        },
        
        confirmDel() {
            RedefinicionFila.deleteRedefinicionFila(this.rowId)
            .then((res) => { 
                this.$toast.success(res.data.message)
                this.consultaRedefinicionFilas();
            })
            .catch((err) => { 
                console.log(err) 
                this.$toast.error('Error al eliminar la redefinición de fila');
            })
        },
        
        borraCriterio() {
            if (!this.fgl_consulta) return;
            this.listRedefinicionFila = this.listRedefinicionFilaOrigen || this.listRedefinicionFila
            this.visibleItems = [...this.listRedefinicionFila];
            if (this.$refs.corporateTable) {
                this.$refs.corporateTable.clearFilters();
            }
        },
    },
    watch: {
        selVigencia() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.');
            }
        }
    },
    mounted() {
        this.isLoading = true;
        
        TipoReporte.getAllTipoReporte() 
        .then((res) => { 
            this.reportes = res.data.typeReport || [];
        })
        .catch((err) => console.log(err))

        PlanConsolidacion.getVigenciaPlanCons() 
        .then((res) => { 
            this.listVigencia = res.data.getVigencia || [];
        })
        .catch((err) => { 
            console.log(err) 
        })
        .finally(() => { 
            this.isLoading = false;
        })
    }
}
</script>

<style src="../../main.css"></style>

<style scoped>
/* ... estilos existentes ... */
.plan-cuentas-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.plan-cuentas-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.plan-cuentas-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.plan-cuentas-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.plan-cuentas-header .consult-btn:hover,
.plan-cuentas-header .consult-btn:focus,
.plan-cuentas-header .consult-btn:active,
.plan-cuentas-header .consult-btn:not(:disabled):hover,
.plan-cuentas-header .consult-btn:not(:disabled):focus,
.plan-cuentas-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.plan-cuentas-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 490;
}

.table-action-btn {
    border: none !important;
    border-radius: 4px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    min-width: 30px !important;
    transition: all 0.2s;
}

.table-action-btn i {
    display: flex;
    align-items: center;
    justify-content: center;
}

.table-action-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.reset-btn-danger {
    background-color: #e74c3c !important;
    color: white !important;
    border: none !important;
    border-radius: 4px !important;
}

.reset-btn-danger:hover:not(:disabled) {
    background-color: #c0392b !important;
}

.plan-cuentas-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #28a745;
    color: white;
    border: 1px solid #28a745;
}

.table-toolbar-add-btn:hover:not(:disabled) {
    background-color: #218838;
    border-color: #1e7e34;
}

.table-toolbar-add-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

@media (max-width: 1200px) {
    .plan-cuentas-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    .plan-cuentas-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .plan-cuentas-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    .plan-cuentas-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    .plan-cuentas-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    .plan-cuentas-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    .plan-cuentas-header .filters-container {
        gap: 4px;
    }
    .plan-cuentas-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }
}
</style>